(function (root) {
  'use strict';

  const LOCALES = new Set(['en', 'fr', 'it']);
  const SORTS = new Set(['title-ascending', 'title-descending']);
  const CACHE_TTL = 60000;
  const CACHE_LIMIT = 4;

  function abortError() {
    const error = new Error('The collection request was superseded.');
    error.name = 'AbortError';
    return error;
  }

  function checkSignal(signal) {
    if (signal && signal.aborted) throw abortError();
  }

  async function fetchSection(url, signal, fetcher = root.fetch.bind(root), timeoutMs = 20000) {
    checkSignal(signal);
    const controller = new AbortController();
    let rejectAbort;
    const aborted = new Promise((resolve, reject) => { rejectAbort = reject; });
    const abort = () => {
      controller.abort();
      rejectAbort(abortError());
    };
    signal?.addEventListener('abort', abort, { once: true });
    let timedOut = false;
    let timer;
    const timeoutError = new Error('The collection request timed out.');
    timeoutError.name = 'TimeoutError';
    const timeout = new Promise((resolve, reject) => {
      timer = setTimeout(() => {
        timedOut = true;
        controller.abort();
        reject(timeoutError);
      }, timeoutMs);
    });
    try {
      const request = fetcher(url, { signal: controller.signal, credentials: 'same-origin' }).then((response) => {
        if (!response.ok) throw new Error(`Collection section request failed (${response.status}).`);
        return response.text();
      });
      return await Promise.race([request, timeout, aborted]);
    } catch (error) {
      checkSignal(signal);
      if (timedOut) throw timeoutError;
      throw error;
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener('abort', abort);
    }
  }

  function normalizedLocale(locale) {
    return String(locale || '').toLowerCase().split('-')[0];
  }

  function isLocalizedSort(locale, searchParams, defaultSort) {
    const params = new URLSearchParams(searchParams);
    return LOCALES.has(normalizedLocale(locale)) && SORTS.has(params.get('sort_by') || defaultSort);
  }

  function catalogueKey(path, searchParams, locale, context, pageSize, total) {
    const params = new URLSearchParams(searchParams);
    ['page', 'sort_by', 'section_id', 'sections'].forEach((key) => params.delete(key));
    const entries = [...params.entries()].sort((a, b) =>
      a[0] === b[0] ? (a[1] < b[1] ? -1 : a[1] > b[1] ? 1 : 0) : (a[0] < b[0] ? -1 : 1)
    );
    return JSON.stringify([path, entries, locale, context, pageSize, total]);
  }

  function pageURL(path, searchParams, sectionId, page, origin) {
    const url = new URL(path, origin);
    url.search = searchParams;
    url.searchParams.delete('sections');
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('page', String(page));
    return url;
  }

  function validatePage(page, expected, requestedPage) {
    const validInteger = (value) => Number.isSafeInteger(value) && value >= 0;
    if (!validInteger(page.total) || page.total > 25000 || !validInteger(page.pageSize) || page.pageSize < 1 ||
        !validInteger(page.page) || page.page < 1 || !validInteger(page.pages) ||
        page.pages !== Math.max(1, Math.ceil(page.total / page.pageSize)) ||
        page.page !== requestedPage || !Array.isArray(page.items)) {
      throw new Error('Invalid collection pagination metadata.');
    }
    if (expected && (page.total !== expected.total || page.pageSize !== expected.pageSize ||
        page.pages !== expected.pages || page.locale !== expected.locale || page.context !== expected.context)) {
      throw new Error('The collection changed while its pages were being loaded.');
    }
    const expectedItems = Math.max(0, Math.min(page.pageSize, page.total - (page.page - 1) * page.pageSize));
    if (page.items.length !== expectedItems) throw new Error('An incomplete collection page was returned.');
    for (const item of page.items) {
      if (!item.id || typeof item.title !== 'string' || !item.title.trim()) {
        throw new Error('A product card is missing its sort metadata.');
      }
    }
  }

  async function collectPages(seed, loadPage, signal, concurrency = 3) {
    checkSignal(signal);
    validatePage(seed, null, seed.page);
    const pages = new Map([[seed.page, seed]]);
    const pending = Array.from({ length: seed.pages }, (_, index) => index + 1).filter((page) => page !== seed.page);
    let cursor = 0;
    let failed = false;
    async function worker() {
      while (cursor < pending.length) {
        if (failed) return;
        checkSignal(signal);
        const pageNumber = pending[cursor++];
        try {
          const page = await loadPage(pageNumber);
          checkSignal(signal);
          validatePage(page, seed, pageNumber);
          pages.set(pageNumber, page);
        } catch (error) {
          failed = true;
          throw error;
        }
      }
    }
    await Promise.all(Array.from({ length: Math.min(Math.max(1, concurrency), pending.length) }, worker));
    checkSignal(signal);
    const items = [];
    const ids = new Set();
    for (let page = 1; page <= seed.pages; page += 1) {
      const result = pages.get(page);
      if (!result) throw new Error('A collection page is missing.');
      for (const item of result.items) {
        const id = String(item.id);
        if (ids.has(id)) throw new Error('A product occurred on more than one collection page.');
        ids.add(id);
        items.push(item);
      }
    }
    if (items.length !== seed.total) throw new Error('The complete collection could not be verified.');
    return items;
  }

  function sortItems(items, locale, descending) {
    const collator = new Intl.Collator(locale, { numeric: true, sensitivity: 'base' });
    const sorted = items.slice().sort((a, b) => {
      const titleOrder = collator.compare(a.title.trim().normalize('NFC'), b.title.trim().normalize('NFC'));
      if (titleOrder) return titleOrder;
      const aId = String(a.id);
      const bId = String(b.id);
      return aId < bId ? -1 : aId > bId ? 1 : 0;
    });
    return descending ? sorted.reverse() : sorted;
  }

  const core = { abortError, checkSignal, fetchSection, normalizedLocale, isLocalizedSort, catalogueKey, pageURL, validatePage, collectPages, sortItems };
  if (typeof module === 'object' && module.exports) module.exports = core;
  if (!root.document) return;

  const document = root.document;
  const cache = new Map();
  const hiddenElements = new WeakMap();
  let currentGeneration = 0;
  let paginationBound = false;

  function parsePage(html) {
    const template = document.createElement('template');
    template.innerHTML = html;
    const container = template.content.querySelector('#ProductGridContainer');
    const grid = container && container.querySelector('#product-grid');
    if (!container || !grid) throw new Error('The collection section is missing.');
    const data = container.dataset;
    const page = {
      template, container, grid,
      locale: data.miromiSortLocale,
      context: data.miromiSortContext || '',
      total: Number(data.miromiSortTotal),
      pageSize: Number(data.miromiSortPageSize),
      page: Number(data.miromiSortPage),
      pages: Math.max(1, Number(data.miromiSortPages)),
      sectionId: grid.dataset.id,
      items: [...grid.children].filter((element) => element.tagName === 'LI').map((element) => ({
        id: element.dataset.miromiProductId,
        title: element.dataset.miromiProductTitle,
        html: element.outerHTML,
      })),
    };
    if (!page.sectionId || !page.locale || !data.miromiSortTotal || !data.miromiSortPageSize ||
        !data.miromiSortPage || data.miromiSortPages === undefined) {
      throw new Error('Collection sort metadata is missing.');
    }
    return page;
  }

  function containerNow() {
    return document.getElementById('ProductGridContainer');
  }

  function isActive(searchParams) {
    const container = containerNow();
    return Boolean(container && isLocalizedSort(container.dataset.miromiSortLocale, searchParams, container.dataset.miromiSortDefault));
  }

  function hideResults(container) {
    container.querySelectorAll('#product-grid, .pagination-wrapper').forEach((element) => {
      if (!hiddenElements.has(element)) hiddenElements.set(element, {
        hidden: element.hidden,
        display: element.style.getPropertyValue('display'),
        priority: element.style.getPropertyPriority('display'),
      });
      element.hidden = true;
      element.style.setProperty('display', 'none', 'important');
    });
  }

  function restoreResults(container) {
    container.querySelectorAll('#product-grid, .pagination-wrapper').forEach((element) => {
      const previous = hiddenElements.get(element);
      if (!previous) return;
      element.hidden = previous.hidden;
      if (previous.display) element.style.setProperty('display', previous.display, previous.priority);
      else element.style.removeProperty('display');
      hiddenElements.delete(element);
    });
  }

  function status(container, state, retry) {
    let box = container.querySelector('.miromi-sort-status');
    if (!box) {
      box = document.createElement('div');
      box.className = 'miromi-sort-status page-width';
      box.style.paddingBlock = '1.5rem';
      container.prepend(box);
    }
    box.replaceChildren();
    box.setAttribute('role', state === 'error' ? 'alert' : 'status');
    box.setAttribute('aria-live', state === 'error' ? 'assertive' : 'polite');
    const message = document.createElement('p');
    message.textContent = state === 'error' ? container.dataset.miromiSortError : container.dataset.miromiSortLoading;
    box.append(message);
    if (retry) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'button button--secondary';
      button.textContent = container.dataset.miromiSortRetry;
      button.addEventListener('click', retry, { once: true });
      box.append(button);
    }
  }

  function begin(searchParams, generation) {
    currentGeneration = generation;
    const container = containerNow();
    if (!container) return;
    if (!isActive(searchParams)) {
      container.dataset.miromiSortState = 'native';
      container.removeAttribute('aria-busy');
      container.querySelector('.miromi-sort-status')?.remove();
      restoreResults(container);
      return;
    }
    container.dataset.miromiSortState = 'loading';
    container.setAttribute('aria-busy', 'true');
    hideResults(container);
    status(container, 'loading');
  }

  async function transform(html, searchParams, generation, signal) {
    checkSignal(signal);
    if (generation !== currentGeneration) throw abortError();
    if (!isActive(searchParams)) return html;
    const seed = parsePage(html);
    validatePage(seed, null, seed.page);
    const key = catalogueKey(root.location.pathname, searchParams, seed.locale, seed.context, seed.pageSize, seed.total);
    const now = Date.now();
    for (const [cacheKey, entry] of cache) if (now - entry.time >= CACHE_TTL) cache.delete(cacheKey);
    let items = cache.get(key)?.items;
    if (items) {
      const cachedTitles = new Map(items.map((item) => [String(item.id), item.title]));
      if (seed.items.some((item) => cachedTitles.get(String(item.id)) !== item.title)) {
        cache.delete(key);
        items = null;
      }
    }
    if (!items) {
      items = await collectPages(seed, async (page) => {
        const url = pageURL(root.location.pathname, searchParams, seed.sectionId, page, root.location.origin);
        const result = parsePage(await fetchSection(url.toString(), signal));
        if (result.sectionId !== seed.sectionId) throw new Error('An unexpected collection section was returned.');
        return result;
      }, signal);
      checkSignal(signal);
      if (generation !== currentGeneration) throw abortError();
      cache.set(key, { time: Date.now(), items });
      while (cache.size > CACHE_LIMIT) cache.delete(cache.keys().next().value);
    }
    checkSignal(signal);
    if (generation !== currentGeneration) throw abortError();
    const params = new URLSearchParams(searchParams);
    const descending = (params.get('sort_by') || seed.container.dataset.miromiSortDefault) === 'title-descending';
    const sorted = sortItems(items, seed.locale, descending);
    const visible = sorted.slice((seed.page - 1) * seed.pageSize, seed.page * seed.pageSize);
    if (seed.total > 0 && seed.page <= seed.pages) {
      const cards = document.createElement('template');
      cards.innerHTML = visible.map((item) => item.html).join('');
      const presentStyles = new Set([...cards.content.querySelectorAll('link[rel="stylesheet"]')]
        .map((link) => link.getAttribute('href')));
      for (const link of seed.grid.querySelectorAll('link[rel="stylesheet"]')) {
        if (!presentStyles.has(link.getAttribute('href'))) {
          seed.grid.before(link.cloneNode(true));
          presentStyles.add(link.getAttribute('href'));
        }
      }
      [...cards.content.children].forEach((card, index) => {
        if (card.style.getPropertyValue('--animation-order')) card.style.setProperty('--animation-order', index + 1);
      });
      seed.grid.replaceChildren(cards.content);
    }
    seed.container.dataset.miromiSortState = 'ready';
    return seed.template.innerHTML;
  }

  function afterRender() {
    const container = containerNow();
    if (!container) return;
    container.removeAttribute('aria-busy');
    container.querySelector('.miromi-sort-status')?.remove();
    restoreResults(container);
  }

  function fail(error, retry) {
    if (error?.name === 'AbortError') return;
    const container = containerNow();
    if (!container) return;
    container.dataset.miromiSortState = 'error';
    container.removeAttribute('aria-busy');
    hideResults(container);
    status(container, 'error', retry);
  }

  function bindPagination(renderPage) {
    if (paginationBound) return;
    paginationBound = true;
    document.addEventListener('click', (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = event.target.closest('#ProductGridContainer .pagination a[href]');
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return;
      const url = new URL(anchor.href, root.location.href);
      if (url.origin !== root.location.origin || url.pathname !== root.location.pathname || !isActive(url.search)) return;
      event.preventDefault();
      Promise.resolve(renderPage(url.search.slice(1), event)).then(() => {
        const container = containerNow();
        if (container?.dataset.miromiSortState === 'ready') {
          const hadTabindex = container.hasAttribute('tabindex');
          if (!hadTabindex) container.setAttribute('tabindex', '-1');
          container.focus({ preventScroll: true });
          if (!hadTabindex) container.addEventListener('blur', () => container.removeAttribute('tabindex'), { once: true });
          container.scrollIntoView({ block: 'start' });
        }
      });
    });
  }

  root.MiromiLocalizedSort = { isActive, begin, transform, afterRender, fail, bindPagination, fetchSection, clearCache: () => cache.clear() };
})(typeof window !== 'undefined' ? window : globalThis);
